import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Handshake, Plus, FilePdf, FloppyDisk, Trash, MagnifyingGlass,
  CopySimple, X, Upload, Info, PencilSimple, DownloadSimple,
  CaretUp, CaretDown, Check, PaperPlaneTilt, CheckCircle, XCircle,
  ClipboardText, Play, Flag, Prohibit,
} from '@phosphor-icons/react';
import RowActionsMenu from '../../components/common/RowActionsMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCA_URL = `${API}/subcontractor-agreements`;

function getToken() {
  return localStorage.getItem('erp_token')
    || localStorage.getItem('access_token')
    || localStorage.getItem('token');
}

const STATUS_COLOR = {
  draft: 'bg-slate-100 text-slate-700 border-slate-300',
  pending_approval: 'bg-amber-50 text-amber-700 border-amber-300',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  signed: 'bg-blue-50 text-blue-700 border-blue-300',
  in_progress: 'bg-indigo-50 text-indigo-700 border-indigo-300',
  completed: 'bg-teal-50 text-teal-700 border-teal-300',
  rejected: 'bg-rose-50 text-rose-700 border-rose-300',
  terminated: 'bg-red-50 text-red-700 border-red-300',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-300', // legacy alias
};

const STATUS_LABEL = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  signed: 'Both Parties Signed',
  in_progress: 'Job In Progress',
  completed: 'Job Completed',
  rejected: 'Rejected',
  terminated: 'Terminated',
  active: 'Active',
};

const DEFAULT_SECTIONS = [
  'Preamble', 'Scope of Works', 'Specifications', 'Deliverables',
  'Work Schedule', 'Payment Terms', 'HSE (Health, Safety & Environment)',
  'Insurance', 'Warranty', 'Defect Liability Period', 'Approvals',
  'Termination Clause', 'Confidentiality', 'Dispute Resolution', 'General Terms',
];

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

// -------- Party form block (reused for first + second parties) ---------
function PartyBlock({ title, value, onChange, suppliers, allowPickFromSuppliers = false }) {
  return (
    <div className="rounded-md border border-border/60 bg-muted/30 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{title}</Label>
        {allowPickFromSuppliers && (
          <select
            className="text-xs bg-background border border-input rounded px-2 py-1"
            value=""
            onChange={(e) => {
              const s = suppliers.find(x => x.id === e.target.value);
              if (!s) return;
              onChange({
                ...value,
                supplier_id: s.id,
                name: s.name || value.name,
                address: s.address || value.address,
                contact_person: s.contact_person || value.contact_person,
                contact_number: s.phone || value.contact_number,
                email: s.email || value.email,
                trn: s.tax_id || s.trn || value.trn,
              });
              toast.success(`Loaded from ${s.name}`);
            }}
            data-testid={`${title.replace(/\s/g,'-').toLowerCase()}-supplier-picker`}
          >
            <option value="">Pick from suppliers…</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </div>
      <Input
        placeholder="Name"
        value={value.name || ''}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
        data-testid={`${title.replace(/\s/g,'-').toLowerCase()}-name`}
      />
      <Textarea
        placeholder="Address"
        rows={2}
        value={value.address || ''}
        onChange={(e) => onChange({ ...value, address: e.target.value })}
        data-testid={`${title.replace(/\s/g,'-').toLowerCase()}-address`}
      />
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Contact person" value={value.contact_person || ''} onChange={(e) => onChange({ ...value, contact_person: e.target.value })} />
        <Input placeholder="Phone" value={value.contact_number || ''} onChange={(e) => onChange({ ...value, contact_number: e.target.value })} />
        <Input placeholder="Email" value={value.email || ''} onChange={(e) => onChange({ ...value, email: e.target.value })} />
        <Input placeholder="TRN / Tax ID" value={value.trn || ''} onChange={(e) => onChange({ ...value, trn: e.target.value })} />
      </div>
    </div>
  );
}

// -------- Rich-text section (contentEditable — no extra library) --------
function SectionEditor({ section, onChange, onDelete }) {
  const [heading, setHeading] = useState(section.heading || '');
  useEffect(() => setHeading(section.heading || ''), [section.heading]);
  return (
    <div className="rounded-md border border-border/60 bg-background overflow-hidden">
      <div className="flex items-center gap-2 bg-muted/50 px-3 py-2 border-b border-border/60">
        <Input
          className="flex-1 h-8 bg-background"
          value={heading}
          onChange={(e) => { setHeading(e.target.value); onChange({ ...section, heading: e.target.value }); }}
          placeholder="Section heading"
          data-testid={`section-heading-${section.id}`}
        />
        {!section.is_standard && (
          <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`delete-section-${section.id}`}>
            <Trash size={14} />
          </Button>
        )}
      </div>
      <div
        contentEditable
        suppressContentEditableWarning
        className="min-h-[80px] px-3 py-2 text-sm outline-none focus:bg-muted/10"
        dangerouslySetInnerHTML={{ __html: section.content || '' }}
        onBlur={(e) => onChange({ ...section, content: e.currentTarget.innerHTML })}
        data-testid={`section-content-${section.id}`}
      />
    </div>
  );
}

// -------- Main page -----------------------------------------------------
export default function SubcontractorAgreementsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [prefix, setPrefix] = useState('SCA');
  // Attachment rename / reorder UI state
  const [renamingAttId, setRenamingAttId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');

  const axiosOpts = useMemo(() => ({
    headers: { Authorization: `Bearer ${getToken()}` },
  }), []);

  // Persist current attachment order to the backend so it drives the PDF too.
  const persistAttachmentOrder = async (docId, orderedIds) => {
    try {
      await axios.post(
        `${API}/documents/attachments/subcontractor_agreements/${docId}/reorder`,
        { order: orderedIds },
        axiosOpts,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reorder failed');
    }
  };

  const moveAttachment = async (fromIdx, toIdx) => {
    if (!editing?.id) return;
    const list = Array.isArray(editing.attachments) ? [...editing.attachments] : [];
    if (toIdx < 0 || toIdx >= list.length || fromIdx === toIdx) return;
    const [row] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, row);
    setEditing({ ...editing, attachments: list });
    await persistAttachmentOrder(editing.id, list.map(a => a.id));
  };

  const saveRename = async (attId) => {
    const draft = (renameDraft || '').trim();
    if (!draft) { toast.error('Name cannot be empty'); return; }
    try {
      await axios.patch(`${API}/documents/attachments/${attId}`, { custom_name: draft }, axiosOpts);
      const updated = (editing.attachments || []).map(a =>
        a.id === attId ? { ...a, custom_name: draft } : a
      );
      setEditing({ ...editing, attachments: updated });
      setRenamingAttId(null);
      setRenameDraft('');
      toast.success('Attachment renamed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${SCA_URL}/`, axiosOpts);
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load agreements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    axios.get(`${SCA_URL}/settings/prefix`, axiosOpts).then(r => setPrefix(r.data.prefix)).catch(() => {});
    axios.get(`${API}/suppliers`, axiosOpts).then(r => setSuppliers(r.data || [])).catch(() => {});
  }, [axiosOpts]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.reference_number || '').toLowerCase().includes(t)
      || (r.title || '').toLowerCase().includes(t)
      || (r.second_party?.name || '').toLowerCase().includes(t)
      || (r.project_name || '').toLowerCase().includes(t)
    );
  }, [rows, search]);

  const openCreate = () => {
    setEditing({
      mode: 'structured',
      title: '',
      project_name: '',
      agreement_date: new Date().toISOString().slice(0, 10),
      start_date: '',
      end_date: '',
      currency: 'AED',
      total_value: 0,
      first_party: { name: '', address: '', contact_person: '', contact_number: '', email: '', trn: '' },
      second_party: { name: '', address: '', contact_person: '', contact_number: '', email: '', trn: '' },
      sections: DEFAULT_SECTIONS.map((h, i) => ({
        id: `s${i}-${Math.random().toString(36).slice(2, 8)}`,
        heading: h, content: '', order: i, is_standard: true,
      })),
      wrapped_pdf_base64: null,
      wrapped_original_filename: '',
      layout_template: 'classic',
      print_style: 'branded',
      notes: '',
      attachments: [],
    });
    setModalOpen(true);
  };

  const openEdit = async (id) => {
    try {
      const r = await axios.get(`${SCA_URL}/${id}`, axiosOpts);
      const data = r.data || {};
      // Fetch attachments list for this agreement (uses standard doc-attachments route)
      try {
        const attRes = await axios.get(`${API}/documents/attachments/subcontractor_agreements/${id}`, axiosOpts);
        data.attachments = attRes.data?.attachments || attRes.data || [];
      } catch { data.attachments = []; }
      setEditing(data);
      setModalOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load agreement');
    }
  };

  const saveAgreement = async () => {
    if (!editing.title) { toast.error('Title is required'); return; }
    if (!editing.first_party?.name) { toast.error('First party name is required'); return; }
    if (!editing.second_party?.name) { toast.error('Second party name is required'); return; }
    if (editing.mode === 'wrapped' && !editing.wrapped_pdf_base64 && !editing.id) {
      toast.error('Please upload a Word/PDF file for Option 2');
      return;
    }
    try {
      let saved;
      if (editing.id) {
        // Strip wrapped_pdf_base64 if unchanged (avoid re-uploading same blob)
        const payload = { ...editing };
        if (!editing._wrap_changed) delete payload.wrapped_pdf_base64;
        // Post-draft edits must include an audit reason
        const postApproval = editing.status && !['draft', 'rejected'].includes(editing.status);
        if (postApproval) {
          const reason = window.prompt(
            `This agreement is currently "${STATUS_LABEL[editing.status] || editing.status}". ` +
            `Editing it will reset its status back to DRAFT and require a fresh approval.\n\n` +
            `Please enter the reason for editing:`
          );
          if (!reason || !reason.trim()) {
            toast.info('Edit cancelled — reason is required');
            return;
          }
          payload.edit_after_approval_reason = reason.trim();
        }
        const r = await axios.put(`${SCA_URL}/${editing.id}`, payload, axiosOpts);
        saved = r.data;
      } else {
        const r = await axios.post(`${SCA_URL}/`, editing, axiosOpts);
        saved = r.data;
      }
      toast.success(`Agreement ${saved.reference_number} saved`);
      setModalOpen(false);
      setEditing(null);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this agreement? It will be moved to the recycle bin.')) return;
    try {
      await axios.delete(`${SCA_URL}/${id}`, axiosOpts);
      toast.success('Deleted');
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const duplicate = async (id) => {
    try {
      await axios.post(`${SCA_URL}/${id}/duplicate`, {}, axiosOpts);
      toast.success('Duplicated');
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Duplicate failed'); }
  };

  const downloadPdf = async (id, ref) => {
    try {
      const r = await axios.get(`${SCA_URL}/${id}/pdf`, { ...axiosOpts, responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${ref || 'agreement'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('PDF download failed'); }
  };

  const previewPdf = (id) => {
    const url = `${SCA_URL}/${id}/pdf`;
    // Preview in a new tab with Authorization header via blob
    axios.get(url, { ...axiosOpts, responseType: 'blob' }).then(r => {
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
    }).catch(() => toast.error('Failed to open PDF'));
  };

  // ==================== Status workflow transitions ==================
  //
  // Each helper calls the corresponding backend endpoint, refreshes the
  // row list, and shows a toast. Reject / Terminate demand a reason.
  const transitionSCA = async (id, action, opts = {}) => {
    const { promptReason, successMsg, extraBody = {} } = opts;
    let reason;
    if (promptReason) {
      reason = window.prompt(`Enter ${promptReason}:`);
      if (!reason || !reason.trim()) {
        toast.info('Cancelled — reason is required');
        return;
      }
    }
    try {
      await axios.post(
        `${SCA_URL}/${id}/${action}`,
        { reason, ...extraBody },
        axiosOpts,
      );
      toast.success(successMsg || 'Status updated');
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transition failed');
    }
  };

  const submitForApproval = (id) => transitionSCA(id, 'submit', {
    successMsg: 'Submitted for internal approval',
  });
  const approveSCA = (id) => transitionSCA(id, 'approve', {
    successMsg: 'Agreement approved by Manager',
  });
  const rejectSCA = (id) => transitionSCA(id, 'reject', {
    promptReason: 'rejection reason',
    successMsg: 'Agreement rejected',
  });
  const markSigned = (id) => transitionSCA(id, 'sign', {
    successMsg: 'Marked as signed by both parties',
    extraBody: { signed_date: new Date().toISOString().slice(0, 10) },
  });
  const startJob = (id) => transitionSCA(id, 'start-job', {
    successMsg: 'Job started',
    extraBody: { start_date: new Date().toISOString().slice(0, 10) },
  });
  const completeJob = (id) => transitionSCA(id, 'complete', {
    successMsg: 'Job marked completed',
    extraBody: { completion_date: new Date().toISOString().slice(0, 10) },
  });
  const terminateSCA = (id) => transitionSCA(id, 'terminate', {
    promptReason: 'termination reason',
    successMsg: 'Agreement terminated',
  });


  const handleFileUpload = async (file) => {
    if (!file) return;
    if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
      // Read straight to base64
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = String(reader.result || '').split(',').pop();
        setEditing(prev => ({ ...prev, wrapped_pdf_base64: b64, wrapped_original_filename: file.name, _wrap_changed: true }));
        toast.success('PDF attached — save the agreement to finalise.');
      };
      reader.readAsDataURL(file);
    } else if (/\.docx?$/i.test(file.name)) {
      // Send to backend for DOCX → PDF conversion
      const fd = new FormData();
      fd.append('file', file);
      try {
        toast.info('Converting Word document…');
        const r = await axios.post(`${SCA_URL}/upload-preview`, fd, {
          headers: { ...axiosOpts.headers, 'Content-Type': 'multipart/form-data' },
        });
        setEditing(prev => ({ ...prev, wrapped_pdf_base64: r.data.pdf_base64, wrapped_original_filename: r.data.filename, _wrap_changed: true }));
        toast.success('Converted. Save the agreement to finalise.');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Word conversion failed. Please convert to PDF and upload again.');
      }
    } else {
      toast.error('Only PDF or Word (.docx) files are supported.');
    }
  };

  const savePrefix = async () => {
    const val = window.prompt('New prefix (letters/numbers, max 8 chars):', prefix);
    if (val === null) return;
    try {
      const r = await axios.put(`${SCA_URL}/settings/prefix`, { prefix: val }, axiosOpts);
      setPrefix(r.data.prefix);
      toast.success(`Prefix set to ${r.data.prefix}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Handshake size={26} weight="fill" className="text-primary" />
            <h1 className="text-2xl font-bold">Subcontractor Agreements</h1>
            <Badge variant="outline" className="ml-2">Prefix: {prefix}</Badge>
            <Button size="sm" variant="ghost" onClick={savePrefix} data-testid="sca-prefix-edit">
              <PencilSimple size={14} />
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">Branded agreements for subcontractors and suppliers · Structured or wrapped-upload.</div>
        </div>
        <Button onClick={openCreate} data-testid="sca-new-btn"><Plus size={16} className="mr-2" />New Agreement</Button>
      </div>

      <Card className="p-3 flex items-center gap-2">
        <MagnifyingGlass size={16} className="text-muted-foreground" />
        <Input
          className="max-w-md"
          placeholder="Search by ref, title, project, subcontractor…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="sca-search"
        />
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Ref #</th>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2">Subcontractor</th>
                <th className="text-left px-3 py-2">Mode</th>
                <th className="text-left px-3 py-2">Period</th>
                <th className="text-right px-3 py-2">Value</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No agreements yet. Click <em>New Agreement</em> to create one.</td></tr>
              )}
              {filtered.map((r) => (
                <motion.tr key={r.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border-t border-border/50 hover:bg-muted/30">
                  <td className="px-3 py-2 font-mono text-xs">{r.reference_number}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.title}</div>
                    <div className="text-xs text-muted-foreground">{r.project_name}</div>
                  </td>
                  <td className="px-3 py-2">{r.second_party?.name || '—'}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="text-[10px]">{r.mode === 'wrapped' ? 'Wrapped Upload' : 'Structured'}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</td>
                  <td className="px-3 py-2 text-right">{r.currency} {(r.total_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2">
                    <Badge className={`${STATUS_COLOR[r.status] || STATUS_COLOR.draft}`}>
                      {STATUS_LABEL[r.status] || r.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => previewPdf(r.id)} title="View PDF" data-testid={`sca-view-${r.id}`}><FilePdf size={16} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => downloadPdf(r.id, r.reference_number)} title="Download" data-testid={`sca-download-${r.id}`}><DownloadSimple size={16} /></Button>
                      <RowActionsMenu
                        testId={`sca-actions-${r.id}`}
                        actions={[
                          (r.status === 'draft' || r.status === 'rejected') && {
                            id: 'edit', label: 'Edit', icon: PencilSimple,
                            onClick: () => openEdit(r.id),
                          },
                          (r.status && r.status !== 'draft' && r.status !== 'rejected') && {
                            id: 'edit-reset', label: 'Edit (resets to Draft)', icon: PencilSimple,
                            onClick: () => openEdit(r.id),
                          },
                          'separator',
                          (r.status === 'draft' || r.status === 'rejected') && {
                            id: 'submit', label: 'Submit for Internal Approval', icon: PaperPlaneTilt,
                            onClick: () => submitForApproval(r.id),
                          },
                          r.status === 'pending_approval' && {
                            id: 'approve', label: 'Approve by Manager', icon: CheckCircle,
                            onClick: () => approveSCA(r.id),
                          },
                          r.status === 'pending_approval' && {
                            id: 'reject', label: 'Reject', icon: XCircle,
                            onClick: () => rejectSCA(r.id), destructive: true,
                          },
                          r.status === 'approved' && {
                            id: 'sign', label: 'Both Parties Signed', icon: ClipboardText,
                            onClick: () => markSigned(r.id),
                          },
                          r.status === 'signed' && {
                            id: 'start-job', label: 'Start Job (In Progress)', icon: Play,
                            onClick: () => startJob(r.id),
                          },
                          r.status === 'in_progress' && {
                            id: 'complete', label: 'Mark Job Completed', icon: Flag,
                            onClick: () => completeJob(r.id),
                          },
                          ['approved', 'signed', 'in_progress'].includes(r.status) && {
                            id: 'terminate', label: 'Terminate Agreement', icon: Prohibit,
                            onClick: () => terminateSCA(r.id), destructive: true,
                          },
                          'separator',
                          { id: 'duplicate', label: 'Duplicate', icon: CopySimple,
                            onClick: () => duplicate(r.id) },
                          (r.status === 'draft' || r.status === 'rejected') && {
                            id: 'delete', label: 'Delete', icon: Trash,
                            onClick: () => remove(r.id), destructive: true,
                          },
                        ]}
                      />
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* -------- Create / Edit Modal -------- */}
      <Dialog open={modalOpen} onOpenChange={(v) => { if (!v) { setModalOpen(false); setEditing(null); } }}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Handshake size={20} weight="fill" className="text-primary" />
              {editing?.id ? `Edit ${editing.reference_number}` : 'New Subcontractor Agreement'}
            </DialogTitle>
          </DialogHeader>

          {editing && (
            <ScrollArea className="flex-1 px-4 py-3">
              <div className="space-y-4">
                {/* Mode toggle */}
                <div className="flex items-center gap-2 rounded-md bg-muted/40 p-2 border">
                  <div className="text-xs font-semibold text-muted-foreground px-2">Authoring mode:</div>
                  <Button
                    size="sm"
                    variant={editing.mode === 'structured' ? 'default' : 'outline'}
                    onClick={() => setEditing({ ...editing, mode: 'structured' })}
                    data-testid="mode-structured"
                  >Option 1 · Structured Sections</Button>
                  <Button
                    size="sm"
                    variant={editing.mode === 'wrapped' ? 'default' : 'outline'}
                    onClick={() => setEditing({ ...editing, mode: 'wrapped' })}
                    data-testid="mode-wrapped"
                  >Option 2 · Wrap Uploaded PDF/Word</Button>
                </div>

                {/* Common fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Agreement Title *</Label>
                    <Input value={editing.title || ''} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="sca-title" />
                  </div>
                  <div>
                    <Label className="text-xs">Project / Site</Label>
                    <Input value={editing.project_name || ''} onChange={(e) => setEditing({ ...editing, project_name: e.target.value })} data-testid="sca-project" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Date</Label>
                      <Input type="date" value={editing.agreement_date || ''} onChange={(e) => setEditing({ ...editing, agreement_date: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">Start</Label>
                      <Input type="date" value={editing.start_date || ''} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">End</Label>
                      <Input type="date" value={editing.end_date || ''} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Currency</Label>
                      <Input value={editing.currency || 'AED'} onChange={(e) => setEditing({ ...editing, currency: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">Total Value</Label>
                      <Input type="number" value={editing.total_value || 0} onChange={(e) => setEditing({ ...editing, total_value: parseFloat(e.target.value) || 0 })} />
                    </div>
                  </div>
                </div>

                {/* Parties */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <PartyBlock title="First Party (Company)" value={editing.first_party || {}} onChange={(v) => setEditing({ ...editing, first_party: v })} suppliers={[]} />
                  <PartyBlock title="Second Party (Subcontractor / Supplier)" value={editing.second_party || {}} onChange={(v) => setEditing({ ...editing, second_party: v })} suppliers={suppliers} allowPickFromSuppliers />
                </div>

                {/* Option 1 — Sections */}
                {editing.mode === 'structured' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold">Agreement Sections</Label>
                      <Button size="sm" variant="outline" onClick={() => {
                        const next = [...(editing.sections || [])];
                        next.push({
                          id: `s-${Math.random().toString(36).slice(2, 8)}`,
                          heading: 'New Section', content: '', order: next.length, is_standard: false,
                        });
                        setEditing({ ...editing, sections: next });
                      }} data-testid="sca-add-section">
                        <Plus size={14} className="mr-1" /> Add Section
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {(editing.sections || []).map((s, idx) => (
                        <SectionEditor
                          key={s.id}
                          section={s}
                          onChange={(next) => {
                            const arr = [...editing.sections];
                            arr[idx] = next;
                            setEditing({ ...editing, sections: arr });
                          }}
                          onDelete={() => setEditing({ ...editing, sections: editing.sections.filter((_, i) => i !== idx) })}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Option 2 — Upload (the MAIN agreement document, wrapped with letterhead) */}
                {editing.mode === 'wrapped' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-semibold">Main Agreement Document (Word/PDF)</Label>
                      <Badge variant="outline" className="text-[10px]">wrapped with your letterhead</Badge>
                    </div>
                    <div className="rounded-md border-l-4 border-amber-400 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
                      <Info size={16} className="mt-0.5 flex-shrink-0" />
                      <div>
                        This is the <strong>body of the agreement</strong> — a single Word or PDF file. To change its content, edit the original on your computer and re-upload. Supporting files (drawings, insurance certificates, photos, etc.) go in the <strong>Supporting Attachments</strong> section below.
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-md p-6 bg-muted/20">
                      <Upload size={32} className="text-muted-foreground mb-2" />
                      <input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => handleFileUpload(e.target.files?.[0])} className="text-xs" data-testid="sca-upload-file" />
                      {editing.wrapped_original_filename && (
                        <div className="mt-2 text-xs text-emerald-700">✓ {editing.wrapped_original_filename} — will be wrapped on save.</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Print style + layout toggles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-md border bg-muted/30 p-3">
                    <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2 block">Print Style</Label>
                    <div className="flex gap-2">
                      <Button size="sm" variant={editing.print_style === 'branded' ? 'default' : 'outline'} onClick={() => setEditing({ ...editing, print_style: 'branded' })} data-testid="print-style-branded">Branded (logo + letterhead)</Button>
                      <Button size="sm" variant={editing.print_style === 'plain' ? 'default' : 'outline'} onClick={() => setEditing({ ...editing, print_style: 'plain' })} data-testid="print-style-plain">Plain (for own letterhead paper)</Button>
                    </div>
                  </div>
                  <div className="rounded-md border bg-muted/30 p-3">
                    <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2 block">PDF Layout</Label>
                    <div className="flex gap-2">
                      {['classic', 'modern_band', 'minimalist'].map(l => (
                        <Button key={l} size="sm" variant={(editing.layout_template || 'classic') === l ? 'default' : 'outline'} onClick={() => setEditing({ ...editing, layout_template: l })} data-testid={`layout-${l}`}>
                          {l === 'modern_band' ? 'Modern Band' : (l[0].toUpperCase() + l.slice(1))}
                        </Button>
                      ))}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-2">
                      {editing.print_style === 'plain' ? 'Layout is ignored in Plain mode (no logo/letterhead).' : 'Same visual grammar as your Invoices / Quotations.'}
                    </div>
                  </div>
                </div>

                {/* Supporting attachments (drawings, certificates, photos — separate from the main document) */}
                {editing.id ? (
                  <div className="rounded-md border bg-muted/20 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm font-semibold">Supporting Attachments ({(editing.attachments || []).length})</Label>
                        <Badge variant="outline" className="text-[10px]">drawings · certificates · photos</Badge>
                      </div>
                      <input
                        type="file"
                        multiple
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          if (!files.length) return;
                          let ok = 0, fail = 0;
                          for (const file of files) {
                            const fd = new FormData();
                            fd.append('file', file);
                            try {
                              await axios.post(`${API}/documents/attachments/subcontractor_agreements/${editing.id}`, fd, {
                                headers: { ...axiosOpts.headers, 'Content-Type': 'multipart/form-data' },
                              });
                              ok++;
                            } catch (err) {
                              fail++;
                              toast.error(`${file.name}: ${err.response?.data?.detail || 'upload failed'}`);
                            }
                          }
                          if (ok) toast.success(`${ok} attachment${ok > 1 ? 's' : ''} uploaded${fail ? ` · ${fail} failed` : ''}`);
                          const list = await axios.get(`${API}/documents/attachments/subcontractor_agreements/${editing.id}`, axiosOpts);
                          setEditing({ ...editing, attachments: list.data?.attachments || list.data || [] });
                          e.target.value = '';
                        }}
                        className="text-xs"
                        data-testid="sca-attachment-upload"
                      />
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Multiple files supported — drawings, insurance certificates, photos, notices, etc. These are separate from the main agreement body. Attachments print without letterhead so your originals stay intact.
                    </p>
                    {(editing.attachments || []).length > 0 && (
                      <ul className="text-xs space-y-1" data-testid="sca-attachments-list">
                        {(editing.attachments || []).map((att, idx, arr) => {
                          const displayName = att.custom_name || att.original_filename || att.filename;
                          const isEditing = renamingAttId === att.id;
                          return (
                          <li key={att.id} className="flex items-center justify-between gap-2 border-b border-border/50 py-1">
                            <span className="flex-1 truncate flex items-center gap-2">
                              <span aria-hidden>📎</span>
                              {isEditing ? (
                                <Input
                                  value={renameDraft}
                                  onChange={(e) => setRenameDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); saveRename(att.id); }
                                    else if (e.key === 'Escape') { setRenamingAttId(null); setRenameDraft(''); }
                                  }}
                                  className="h-7 text-xs flex-1"
                                  autoFocus
                                  data-testid={`sca-att-rename-input-${att.id}`}
                                />
                              ) : (
                                <span className="truncate" title={displayName}>
                                  {displayName}
                                  <span className="text-muted-foreground ml-1">({Math.round((att.file_size || 0) / 1024)} KB)</span>
                                </span>
                              )}
                            </span>
                            <div className="flex items-center gap-0.5">
                              {isEditing ? (
                                <>
                                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Save name"
                                    onClick={() => saveRename(att.id)}
                                    data-testid={`sca-att-rename-save-${att.id}`}>
                                    <Check size={14} className="text-emerald-600" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Cancel"
                                    onClick={() => { setRenamingAttId(null); setRenameDraft(''); }}>
                                    <X size={14} />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button size="icon" variant="ghost" className="h-7 w-7"
                                    title="Move up" disabled={idx === 0}
                                    onClick={() => moveAttachment(idx, idx - 1)}
                                    data-testid={`sca-att-move-up-${att.id}`}>
                                    <CaretUp size={14} />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-7 w-7"
                                    title="Move down" disabled={idx === arr.length - 1}
                                    onClick={() => moveAttachment(idx, idx + 1)}
                                    data-testid={`sca-att-move-down-${att.id}`}>
                                    <CaretDown size={14} />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-7 w-7"
                                    title="Rename"
                                    onClick={() => { setRenamingAttId(att.id); setRenameDraft(displayName); }}
                                    data-testid={`sca-att-rename-${att.id}`}>
                                    <PencilSimple size={14} />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Download"
                                    onClick={() => window.open(`${API}/documents/attachments/download/${att.id}`, '_blank')}>
                                    <DownloadSimple size={14} />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Delete" onClick={async () => {
                                    if (!window.confirm('Delete this attachment?')) return;
                                    try {
                                      await axios.delete(`${API}/documents/attachments/${att.id}`, axiosOpts);
                                      toast.success('Attachment deleted');
                                      const list = await axios.get(`${API}/documents/attachments/subcontractor_agreements/${editing.id}`, axiosOpts);
                                      setEditing({ ...editing, attachments: list.data?.attachments || list.data || [] });
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
                                  }}>
                                    <Trash size={14} className="text-red-500" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                ) : (
                  <div className="rounded-md border-l-4 border-blue-400 bg-blue-50 p-3 text-xs text-blue-900">
                    <Info size={14} className="inline mr-1" /> Save the agreement first — you can then upload attachments (drawings, specs, insurance, etc.) here.
                  </div>
                )}

                {/* Notes */}
                <div>
                  <Label className="text-xs">Internal Notes (not printed)</Label>
                  <Textarea rows={2} value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
                </div>
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="p-3 border-t">
            <Button variant="outline" onClick={() => { setModalOpen(false); setEditing(null); }}>
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <Button onClick={saveAgreement} data-testid="sca-save-btn">
              <FloppyDisk size={14} className="mr-1" /> Save Agreement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
